import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { TranslateModule } from '@ngx-translate/core';
import {
  DialogShellComponent,
  FilledButtonComponent,
  SearchInputComponent,
  TagComponent,
  TextButtonComponent,
} from '@custom-components';
import { RecipientCandidate } from '../../mock/alarm-recipient-candidates.mock';

export interface AddRecipientsDialogData {
  /** Candidate pool — active partner users not yet in the recipients list. */
  candidates: RecipientCandidate[];
  /** Ids already in the recipients list — excluded from the candidate pool. */
  excludeIds: string[];
}

@Component({
  selector: 'xemex-add-recipients-dialog',
  imports: [
    MatCheckboxModule,
    TranslateModule,
    DialogShellComponent,
    FilledButtonComponent,
    TextButtonComponent,
    SearchInputComponent,
    TagComponent,
  ],
  templateUrl: './add-recipients-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class AddRecipientsDialogComponent {
  private readonly dialogRef = inject(
    MatDialogRef<AddRecipientsDialogComponent, RecipientCandidate[] | undefined>,
  );
  protected readonly data = inject<AddRecipientsDialogData>(MAT_DIALOG_DATA);

  protected readonly query = signal('');
  protected readonly selectedIds = signal<Set<string>>(new Set());

  protected readonly candidates = computed<RecipientCandidate[]>(() =>
    this.data.candidates.filter(c => !this.data.excludeIds.includes(c.id)),
  );

  protected readonly filteredCandidates = computed(() => {
    const q = this.query().toLowerCase().trim();
    const rows = this.candidates();
    if (!q) return rows;
    return rows.filter(
      c =>
        c.name.toLowerCase().includes(q) ||
        c.email.toLowerCase().includes(q) ||
        c.role.toLowerCase().includes(q),
    );
  });

  protected readonly selectedCount = computed(() => this.selectedIds().size);

  protected readonly isAllSelected = computed(() => {
    const rows = this.filteredCandidates();
    return rows.length > 0 && rows.every(c => this.selectedIds().has(c.id));
  });

  protected initial(name: string): string {
    return name.charAt(0).toUpperCase();
  }

  protected toggleAll(): void {
    const rows = this.filteredCandidates();
    const allSelected = this.isAllSelected();
    this.selectedIds.update(ids => {
      const next = new Set(ids);
      rows.forEach(r => (allSelected ? next.delete(r.id) : next.add(r.id)));
      return next;
    });
  }

  protected toggleOne(id: string): void {
    this.selectedIds.update(ids => {
      const next = new Set(ids);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  protected isSelected(id: string): boolean {
    return this.selectedIds().has(id);
  }

  protected cancel(): void {
    this.dialogRef.close(undefined);
  }

  protected add(): void {
    const selected = this.candidates().filter(c => this.selectedIds().has(c.id));
    if (!selected.length) return;
    this.dialogRef.close(selected);
  }
}
